import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Settings, Briefcase, FileText, Calendar, Home } from "react-feather";

function UpdateGlobalOptions() {
  const API_URL_SAVE = "UpdateGlobalOptions/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/GlobalOptions/Id";

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/FinancialYearMaster`;
  const [state, setState] = useState({
    id: 0,
    formData: { CompanyName: "", F_FinancialYearMaster: "", RequestOnPortal: false },
    isReady: false,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const companyNameRef = useRef(null);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Fn_FillListData(dispatch, setState, "FinancialYearMaster", API_URL + "/Id/0");
        await Fn_DisplayData(dispatch, setState, 1, API_URL_EDIT);
        setState(prevState => ({ ...prevState, isReady: true }));
      } catch (error) {
        console.error("Error initializing data:", error);
        setState(prevState => ({ ...prevState, isReady: true }));
      }
    };
    
    initializeData();
  }, [dispatch, location.state]);

  // Auto-focus on CompanyName field when component mounts
  useEffect(() => {
    if (companyNameRef.current) {
      companyNameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    CompanyName: Yup.string()
      .required("Company name is required")
      .min(2, "Company name must be at least 2 characters")
      .max(100, "Company name must be less than 100 characters"),
    F_FinancialYearMaster: Yup.string()
      .required("Financial year is required"),
    RequestOnPortal: Yup.boolean(),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("authUser"));
    let formData = new FormData();
    formData.append("CompanyName", values.CompanyName || "");
    formData.append("F_FinancialYearMaster", values.F_FinancialYearMaster || "");
    formData.append("RequestOnPortal", values.RequestOnPortal ? "1" : "0");
    formData.append("UserId", obj.uid);

   await Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "globaloptionsid",
      navigate,
      "#"
    );

    Fn_DisplayData(dispatch, setState, 1, API_URL_EDIT);

  };

  // Show loading state if data is not ready
  if (!state.isReady) {
    return (
      <div className="master-form-container" style={{padding: '10px 0'}}>
        <div className="container-fluid px-2">
          <Row className="justify-content-center">
            <Col lg={8} md={10} sm={12}>
              <Card className="master-form-card">
                <CardBody className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 mb-0">Loading global options...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={8} md={10} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={20} color="#6D68CB" />
                    </div>
                    <div>
                      <h4 className="mb-0 fw-bold">Global Options</h4>
                      <p className="text-muted mb-0" style={{fontSize: '0.85rem' ,color:'#000'}}>Configure system-wide settings</p>
                    </div>
                  </div>
             
                </div>
              </CardHeader>
              <CardBody className="master-form-card-body">
                <Formik
                  initialValues={{
                    CompanyName: (state.formData && state.formData.CompanyName) || "",
                    F_FinancialYearMaster: (state.formData && state.formData.F_FinancialYearMaster) || "",
                    RequestOnPortal: (state.formData && state.formData.RequestOnPortal) || false,
                  }}
                  enableReinitialize={true}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      {/* Global Options Configuration */}
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="CompanyName" className="master-form-label">
                            <Briefcase size={16} className="me-2" />
                            Company Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={companyNameRef}
                              className={`master-form-input ${errors.CompanyName && touched.CompanyName ? 'is-invalid' : ''}`}
                              type="text"
                              name="CompanyName"
                              placeholder="Enter company name"
                            />
                            <div className="input-icon">
                              <Briefcase size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="CompanyName" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter your company or organization name
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_FinancialYearMaster" className="master-form-label">
                            <Calendar size={16} className="me-2" />
                            Financial Year <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field 
                              as="select" 
                              className={`master-form-input ${errors.F_FinancialYearMaster && touched.F_FinancialYearMaster ? 'is-invalid' : ''}`} 
                              name="F_FinancialYearMaster"
                            >
                              <option value="">Select Financial Year</option>
                              {state.FinancialYearMaster && state.FinancialYearMaster.map((item) => (
                                <option key={item.Id} value={item.Id}>
                                  {item.Name}
                                </option>
                              ))}
                            </Field>
                            <div className="input-icon">
                              <Calendar size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="F_FinancialYearMaster" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select the current financial year for your organization
                          </div>
                        </div>
                      </div>

                      {/* Portal Settings Section */}
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="RequestOnPortal" className="master-form-label">
                            <Home size={16} className="me-2" />
                            Request On Portal
                          </label>
                          <div className="input-wrapper">
                            <div className="form-check form-switch">
                              <Field
                                type="checkbox"
                                className={`form-check-input ${errors.RequestOnPortal && touched.RequestOnPortal ? 'is-invalid' : ''}`}
                                name="RequestOnPortal"
                                id="RequestOnPortal"
                              />
                              <label className="form-check-label" htmlFor="RequestOnPortal">
                                Enable requests on portal
                              </label>
                            </div>
                          </div>
                          <ErrorMessage name="RequestOnPortal" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Toggle to enable or disable request functionality on the portal
                          </div>
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate(-1)}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            Update Settings
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default UpdateGlobalOptions;
